import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Bookmark,
  Archive,
  RefreshCw,
  Trash2,
  X,
  Banknote,
  Smartphone,
  Landmark,
  Receipt,
  Undo2,
} from 'lucide-react';

import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { useConfirm } from '@/components/ConfirmDialog';
import { formatNPR, parsePriceInput } from '@/components/Money';
import { RefreshButton } from '@/components/RefreshButton';
import { PageShell } from '@/components/PageShell';
import { AlphaSortToggle } from '@/components/AlphaSortToggle';
import { useAlphaSort } from '@/lib/useAlphaSort';
import { isValidPhone, normalizePhone, PHONE_HINT } from '@cafe-mgmt/validation';
import { toast } from '@/lib/toast';
import { usePermissions } from '@/lib/permissions';
import { useOverview } from '@/lib/useBranchNav';
import { CombinedCredit, OwedElsewhere } from '@/components/CombinedCredit';
import {
  useHouseTabs,
  useHouseTab,
  useCreateHouseTab,
  useUpdateHouseTab,
  useDeleteHouseTab,
  useCreateHouseTabSettlement,
  useCreateHouseTabWriteOff,
  useReverseHouseTabSettlement,
  type HouseTab,
} from '@/lib/api';

// =========================================================================
// Stakeholder Tabs page.
//
// Each tab is a named running ledger ("Owner A", "Staff meals", "Supplier
// loan"). When an order is closed and charged to a house tab, the tab
// accumulates a balance. Settling the tab = paying it down via cash/online.
// =========================================================================

export function HouseTabsPage() {
  // All-branches mode: one row per customer across branches (linked by phone).
  return useOverview().active ? <CombinedCredit /> : <BranchHouseTabsPage />;
}

function BranchHouseTabsPage() {
  const { can } = usePermissions();
  const tabs = useHouseTabs();
  const create = useCreateHouseTab();
  const [showNew, setShowNew] = useState(false);
  // ?open=<id> opens an account directly — the all-branches credit view links
  // each branch's part straight to it, ready to take a payment. Read from the
  // URL on every render (not copied into state) so a branch switch on this same
  // route never asks the new branch for the previous branch's account.
  const [params, setParams] = useSearchParams();
  const [localOpen, setLocalOpen] = useState<string | null>(null);
  const openId = params.get('open') ?? localOpen;
  const setOpenId = (id: string | null) => {
    setLocalOpen(id);
    if (params.has('open')) {
      const next = new URLSearchParams(params);
      next.delete('open');
      setParams(next, { replace: true });
    }
  };

  const list = tabs.data ?? [];
  const totalOwed = list.reduce((sum, t) => sum + Math.max(0, t.balance_cents), 0);
  const totalWrittenOff = list.reduce((sum, t) => sum + t.written_off_cents, 0);
  const activeTabs = list.filter((t) => t.is_active);
  const archivedTabs = list.filter((t) => !t.is_active);
  // Archived accounts are closed business. The API returns them (it only sorts
  // them last), so they used to pile up under the live ones and the list got
  // longer every month with rows nobody acts on. Default to the accounts that
  // are actually running; the chip says how many are put away.
  const [status, setStatus] = useState<'active' | 'archived'>('active');
  const shown = status === 'active' ? activeTabs : archivedTabs;
  // The column only appears once there is something to put in it — most cafes
  // never write anything off, and an always-empty column is just noise.
  const anyWrittenOff = shown.some((t) => t.written_off_cents > 0);
  const { sorted, alpha, toggle } = useAlphaSort(shown, (t) => t.name, 'house-tabs');

  return (
    <PageShell
      eyebrow="running ledgers"
      title="Credit"
      actions={
        <>
          {list.length > 0 && <AlphaSortToggle active={alpha} onToggle={toggle} />}
          <RefreshButton
            onClick={() => tabs.refetch()}
            busy={tabs.isFetching}
            label="Refresh credit"
          />
          {can('house_tab:create') && (
            <button
              type="button"
              className="btn primary"
              data-tour="credit-new"
              onClick={() => setShowNew(true)}
            >
              <Plus size={14} strokeWidth={1.5} /> New credit
            </button>
          )}
        </>
      }
    >
      <div className="kpis" style={{ marginBottom: 16 }} data-tour="credit-owed">
        <div className="kpi">
          <div className="label">Outstanding (all credit)</div>
          <div
            className="value"
            style={{ color: totalOwed > 0 ? 'var(--amber-fg)' : 'var(--ink-300)' }}
          >
            {formatNPR(totalOwed)}
          </div>
          <div className="delta">money owed to the cafe</div>
        </div>
        <div className="kpi">
          <div className="label">Active accounts</div>
          <div className="value">{activeTabs.length}</div>
        </div>
        <div className="kpi">
          <div className="label">Archived</div>
          <div className="value" style={{ color: 'var(--ink-400)' }}>
            {archivedTabs.length}
          </div>
          {/* The money the cafe decided it would never get. Worth a line of its
              own: it is invisible everywhere else by design — it is not a sale,
              not a collection, and never touches an account. */}
          {totalWrittenOff > 0 && (
            <div className="delta">{formatNPR(totalWrittenOff)} written off</div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Credit</h3>
          <span className="meta">Click an account to view its ledger or settle</span>
        </div>

        {/* Same Active/Archived split the Staff roster uses, for the same
            reason: the archived rows are history, not a worklist. */}
        {list.length > 0 && (
          <div className="filter-row credit-statusfilter">
            <button
              type="button"
              className={`chip ${status === 'active' ? 'active' : ''}`}
              aria-pressed={status === 'active'}
              onClick={() => setStatus('active')}
            >
              Active <span className="chip-count">{activeTabs.length}</span>
            </button>
            <button
              type="button"
              className={`chip ${status === 'archived' ? 'active' : ''}`}
              aria-pressed={status === 'archived'}
              onClick={() => setStatus('archived')}
            >
              Archived <span className="chip-count">{archivedTabs.length}</span>
            </button>
          </div>
        )}

        {tabs.isPending && <LoadingState />}
        {tabs.isError && !tabs.data && <ErrorState onRetry={() => tabs.refetch()} />}
        {tabs.data && list.length === 0 && (
          <EmptyState
            icon={<Bookmark size={28} strokeWidth={1.4} style={{ color: 'var(--amber-fg)' }} />}
            title="No credit accounts yet"
            hint="Add an account for each customer or stakeholder you want to track separately — e.g. an owner, a regular running on credit, or a staff-meals bucket. Close orders to an account and settle them at month-end."
          />
        )}

        {list.length > 0 && sorted.length === 0 && (
          <div className="empty-state">
            {status === 'active'
              ? 'No active credit accounts — every account is archived.'
              : 'No archived accounts.'}
          </div>
        )}

        {sorted.length > 0 && (
          <table className="t">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ textAlign: 'right' }}>Charged</th>
                <th style={{ textAlign: 'right' }}>Settled</th>
                {anyWrittenOff && <th style={{ textAlign: 'right' }}>Written off</th>}
                <th style={{ textAlign: 'right' }}>Balance</th>
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setOpenId(t.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{t.name}</strong>
                    {t.contact_phone && (
                      <div style={{ fontSize: 11, color: 'var(--ink-300)', marginTop: 2 }}>
                        {t.contact_phone}
                      </div>
                    )}
                    {t.notes && (
                      <div style={{ fontSize: 11, color: 'var(--ink-400)', marginTop: 2 }}>
                        {t.notes}
                      </div>
                    )}
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {formatNPR(t.charged_cents)}
                  </td>
                  <td
                    className="num"
                    style={{ textAlign: 'right', color: 'var(--lime-fg)' }}
                  >
                    {formatNPR(t.settled_cents)}
                  </td>
                  {anyWrittenOff && (
                    <td className="num" style={{ textAlign: 'right', color: 'var(--ink-400)' }}>
                      {t.written_off_cents > 0 ? formatNPR(t.written_off_cents) : '—'}
                    </td>
                  )}
                  <td
                    className="num"
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: t.balance_cents > 0 ? 'var(--amber-fg)' : 'var(--ink-300)',
                    }}
                  >
                    {formatNPR(t.balance_cents)}
                  </td>
                  <td>
                    <span className={`pill ${t.is_active ? 'ok' : ''}`}>
                      {t.is_active ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="btn icon"
                        onClick={() => setOpenId(t.id)}
                        aria-label="open"
                      >
                        <Receipt size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <NewTabModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(values);
            toast.success('Credit account created', values.name);
            setShowNew(false);
          } catch (e: unknown) {
            toast.error('Could not create', (e as { message?: string }).message);
          }
        }}
        pending={create.isPending}
      />

      {openId && <DetailModal id={openId} onClose={() => setOpenId(null)} />}
    </PageShell>
  );
}

// -------------------------------------------------------------------------
// New tab modal
// -------------------------------------------------------------------------

function NewTabModal({
  open,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (v: {
    name: string;
    notes: string;
    contact_phone: string;
    opening_balance_cents?: number;
  }) => Promise<void>;
  pending: boolean;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [openingStr, setOpeningStr] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const reset = () => {
    setName('');
    setPhone('');
    setNotes('');
    setOpeningStr('');
    setPhoneError(null);
  };
  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="New credit"
      subtitle="Running ledger for a customer or stakeholder"
    >
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          // A credit account is a debt, and this number is how it gets
          // collected — the API requires it, so catch it before the round trip.
          if (!isValidPhone(phone)) {
            setPhoneError(PHONE_HINT);
            return;
          }
          const openingCents = parsePriceInput(openingStr) ?? 0;
          await onSubmit({
            name: name.trim(),
            notes: notes.trim(),
            contact_phone: normalizePhone(phone),
            ...(openingCents > 0 ? { opening_balance_cents: openingCents } : {}),
          });
          reset();
        }}
      >
        <label>Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Owner A, Staff meals, Supplier loan"
          required
          autoFocus
        />

        <label>Phone</label>
        <input
          inputMode="tel"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); if (phoneError) setPhoneError(null); }}
          placeholder="e.g. 98XXXXXXXX"
          required
        />
        {phoneError
          ? <div className="field-error">{phoneError}</div>
          : <div className="field-hint">How you chase this debt — required.</div>}

        <label>Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="optional — terms, settlement cadence, contact, etc."
        />

        <label>Opening balance owed (optional)</label>
        <input
          inputMode="decimal"
          value={openingStr}
          onChange={(e) => setOpeningStr(e.target.value)}
          placeholder="0"
        />
        <div className="field-hint">
          If this customer already owed you money before you started using this software,
          enter it here — it'll show up as the tab's starting balance.
        </div>

        <div className="modal-actions">
          {/* Clear the form like the ✕ and the backdrop do — this modal keeps
              its state while closed, so a bare onClose left the next "New
              credit" pre-filled with the abandoned one. */}
          <button
            type="button"
            className="btn"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={pending || !name.trim()}>
            {pending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------------------
// Detail modal — shows ledger + settlement form + edit/archive controls.
// -------------------------------------------------------------------------

type DetailMethod = 'cash' | 'online' | 'bank';

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { can } = usePermissions();
  const detail = useHouseTab(id);
  const update = useUpdateHouseTab();
  const del = useDeleteHouseTab();
  const settle = useCreateHouseTabSettlement();
  const reverse = useReverseHouseTabSettlement();
  const writeOff = useCreateHouseTabWriteOff();
  const confirm = useConfirm();

  const [method, setMethod] = useState<DetailMethod>('cash');
  const [amountStr, setAmountStr] = useState('');
  const [refNo, setRefNo] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // Which collection is being reversed, and why. Inline rather than a prompt so
  // the consequence stays on screen while the reason is typed.
  const [reverseId, setReverseId] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  const t = detail.data?.house_tab;
  const balance = t?.balance_cents ?? 0;
  const suggestStr = balance > 0 ? (balance / 100).toString() : '';

  const [woAmountStr, setWoAmountStr] = useState('');
  const [woReason, setWoReason] = useState('');
  const [woErr, setWoErr] = useState<string | null>(null);

  const onWriteOff = async (e: React.FormEvent) => {
    e.preventDefault();
    setWoErr(null);
    const cents = parsePriceInput(woAmountStr) ?? 0;
    if (cents <= 0) {
      setWoErr('amount required');
      return;
    }
    if (cents > balance) {
      setWoErr(`you can't write off more than the ${formatNPR(balance)} outstanding`);
      return;
    }
    const reason = woReason.trim();
    if (!reason) {
      setWoErr('say why — it stays on the ledger');
      return;
    }
    try {
      await writeOff.mutateAsync({ id, amount_cents: cents, reason });
      setWoAmountStr('');
      setWoReason('');
      toast.success('Written off', `${formatNPR(cents)} — no longer owed, and not counted as money in`);
    } catch (e: unknown) {
      setWoErr((e as { message?: string }).message ?? 'Failed');
    }
  };

  const onSettle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const cents = parsePriceInput(amountStr) ?? 0;
    if (cents <= 0) {
      setErr('amount required');
      return;
    }
    if (cents > balance) {
      setErr(
        `amount exceeds outstanding balance of ${formatNPR(balance)} — enter ${formatNPR(balance)} or less`,
      );
      return;
    }
    try {
      await settle.mutateAsync({
        id,
        amount_cents: cents,
        payment_method: method,
        reference_no: refNo.trim(),
        notes: notes.trim(),
      });
      setAmountStr('');
      setRefNo('');
      setNotes('');
      toast.success('Collection recorded', formatNPR(cents));
    } catch (e: unknown) {
      setErr((e as { message?: string }).message ?? 'Failed');
    }
  };

  const onReverse = async (settlementId: string) => {
    const reason = reverseReason.trim();
    if (!reason) return;
    try {
      await reverse.mutateAsync({ id, settlementId, reason });
      setReverseId(null);
      setReverseReason('');
      toast.success('Collection reversed', 'the balance is owed again');
    } catch (e: unknown) {
      toast.error('Could not reverse', (e as { message?: string }).message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t?.name ?? 'Credit'}
      subtitle={
        t?.contact_phone
          ? t.contact_phone
          : t?.is_active
            ? 'Running ledger'
            : 'Archived'
      }
    >
      {detail.isPending && <LoadingState compact />}
      {detail.isError && !detail.data && <ErrorState compact onRetry={() => detail.refetch()} />}
      {detail.data && t && (
        <>
          {t.contact_phone && <OwedElsewhere phone={t.contact_phone} />}
          <div className="settle-totals">
            <div className="settle-row">
              <span>Charged (orders posted to this tab)</span>
              <span className="num">{formatNPR(t.charged_cents)}</span>
            </div>
            <div className="settle-row">
              <span>Settled (paid down)</span>
              <span className="num" style={{ color: 'var(--lime-fg)' }}>
                −{formatNPR(t.settled_cents)}
              </span>
            </div>
            {/* Shown only when there is any, and never folded into "settled":
                both clear the debt, but only one of them is money. */}
            {t.written_off_cents > 0 && (
              <div className="settle-row">
                <span>Written off (not received)</span>
                <span className="num" style={{ color: 'var(--ink-300)' }}>
                  −{formatNPR(t.written_off_cents)}
                </span>
              </div>
            )}
            <hr className="settle-rule" />
            <div className="settle-row bold">
              <span>Balance owed</span>
              <span
                className="num"
                style={{
                  color: balance > 0 ? 'var(--amber-fg)' : 'var(--ink-300)',
                }}
              >
                {formatNPR(balance)}
              </span>
            </div>
          </div>

          {t.notes && (
            <div className="banner-info" style={{ marginTop: 14 }}>
              {t.notes}
            </div>
          )}

          {/* Settle form */}
          {balance > 0 && t.is_active && can('house_tab:settle') && (
            <form onSubmit={onSettle} className="settle-form" style={{ marginTop: 14 }}>
              <label>Record settlement</label>
              <div className="method-grid">
                <button
                  type="button"
                  className={`chip ${method === 'cash' ? 'active' : ''}`}
                  onClick={() => setMethod('cash')}
                >
                  <Banknote size={14} strokeWidth={1.5} /> Cash
                </button>
                <button
                  type="button"
                  className={`chip ${method === 'online' ? 'active' : ''}`}
                  onClick={() => setMethod('online')}
                >
                  <Smartphone size={14} strokeWidth={1.5} /> Online
                </button>
                <button
                  type="button"
                  className={`chip ${method === 'bank' ? 'active' : ''}`}
                  onClick={() => setMethod('bank')}
                >
                  <Landmark size={14} strokeWidth={1.5} /> Bank
                </button>
              </div>
              <div className="field-hint">
                {method === 'cash'
                  ? 'Lands in the cash drawer'
                  : method === 'online'
                    ? 'Lands in the online account'
                    : 'Lands in the bank account'}
              </div>

              <div className="row-inputs">
                <div>
                  <label>Amount (NPR)</label>
                  <input
                    inputMode="decimal"
                    placeholder={suggestStr || '0'}
                    value={amountStr}
                    onChange={(e) => setAmountStr(e.target.value)}
                  />
                  <div className="field-hint">remaining: {formatNPR(balance)}</div>
                </div>
                {method === 'online' && (
                  <div>
                    <label>Txn reference</label>
                    <input
                      value={refNo}
                      onChange={(e) => setRefNo(e.target.value)}
                      placeholder="eSewa / Khalti id"
                    />
                  </div>
                )}
              </div>
              <label>Notes</label>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="optional"
              />

              {err && <div className="banner-error">{err}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => suggestStr && setAmountStr(suggestStr)}
                  disabled={!suggestStr}
                >
                  Auto-fill {suggestStr && `(${formatNPR(balance)})`}
                </button>
                <button type="submit" className="btn primary" disabled={settle.isPending}>
                  {settle.isPending ? 'Recording…' : 'Record settlement'}
                </button>
              </div>
            </form>
          )}

          {/* Write off the remainder. Separate from the settle form above and
              deliberately quieter: this is the rarer, heavier act, and it is the
              only way to clear a balance without money arriving. */}
          {balance > 0 && t.is_active && can('house_tab:write_off') && (
            <form onSubmit={onWriteOff} className="settle-form writeoff-form" style={{ marginTop: 14 }}>
              <label>Write off — money you've decided you won't get</label>
              <p className="field-hint" style={{ marginTop: 0 }}>
                Clears the balance without recording a payment. It never counts as
                money received, and it stays on the ledger with your reason.
              </p>
              <div className="row-inputs">
                <div>
                  <label>Amount</label>
                  <input
                    value={woAmountStr}
                    onChange={(e) => setWoAmountStr(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label>Reason</label>
                  <input
                    value={woReason}
                    onChange={(e) => setWoReason(e.target.value)}
                    placeholder="e.g. goodwill discount, uncollectable"
                  />
                </div>
              </div>
              {woErr && <div className="field-error">{woErr}</div>}
              <div className="modal-actions" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setWoAmountStr((balance / 100).toString())}
                >
                  Whole balance ({formatNPR(balance)})
                </button>
                <button type="submit" className="btn danger" disabled={writeOff.isPending}>
                  {writeOff.isPending ? 'Writing off…' : 'Write off'}
                </button>
              </div>
            </form>
          )}

          {balance === 0 && t.is_active && (
            <div className="banner-info" style={{ marginTop: 14 }}>
              this account is fully settled. archive it if it's no longer in use, or leave it
              open for the next charge.
            </div>
          )}

          {/* Ledger */}
          <Section title={`charges (${detail.data.charges.length})`}>
            {detail.data.charges.length === 0 && (
              <div className="kds-empty">No orders charged to this tab yet.</div>
            )}
            {detail.data.charges.map((c) => (
              <div key={c.payment_id} className="exp" style={{ padding: '10px 0' }}>
                <div className="left">
                  <span className="name">
                    {c.is_opening_balance ? 'Opening balance' : (c.service_table_name ?? 'take-away')}
                  </span>
                  <span className="meta">
                    {new Date(c.recorded_at).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <span className="amt" style={{ color: 'var(--amber-fg)' }}>
                  +{formatNPR(c.amount_cents)}
                </span>
              </div>
            ))}
          </Section>
          <Section title={`collections (${detail.data.settlements.length})`}>
            {detail.data.settlements.length === 0 && (
              <div className="kds-empty">Nothing collected yet.</div>
            )}
            {detail.data.settlements.map((s) => {
              const reversed = !!s.reversed_at;
              const wroteOff = s.kind === 'write_off';
              return (
                <div key={s.id} className="exp settlement-row" style={{ padding: '10px 0' }}>
                  <div className="left">
                    <span className="name" style={reversed ? { textDecoration: 'line-through' } : undefined}>
                      {/* A write-off has no method because no account received
                          anything — printing "cash" here would claim money
                          arrived that never did. */}
                      {wroteOff ? 'written off' : s.payment_method === 'cash' ? 'cash' : 'online'}
                      {s.reference_no && ` · ${s.reference_no}`}
                    </span>
                    <span className="meta">
                      {new Date(s.recorded_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {wroteOff && s.write_off_reason && ` · ${s.write_off_reason}`}
                      {s.notes && ` · ${s.notes}`}
                    </span>
                    {/* A reversal is kept visible with its reason: the ledger has to
                        show what was entered AND what undid it, or a balance that
                        changed twice has no explanation. */}
                    {reversed && (
                      <span className="meta" style={{ color: 'var(--amber-fg)' }}>
                        reversed{s.reversal_reason ? ` — ${s.reversal_reason}` : ''} · counts toward
                        nothing
                      </span>
                    )}
                    {reverseId === s.id && (
                      <form
                        className="row-inputs"
                        style={{ marginTop: 8 }}
                        onSubmit={(e) => {
                          e.preventDefault();
                          void onReverse(s.id);
                        }}
                      >
                        <div>
                          <label>Why is this being reversed?</label>
                          <input
                            autoFocus
                            value={reverseReason}
                            onChange={(e) => setReverseReason(e.target.value)}
                            placeholder="wrong amount / wrong account / duplicate entry"
                          />
                          <div className="field-hint">
                            Recorded on the row and in the activity log. The customer will owe{' '}
                            {formatNPR(s.amount_cents)} again
                            {wroteOff
                              ? '.'
                              : ` and the ${s.payment_method === 'cash' ? 'drawer' : 'account'} gives it back.`}
                          </div>
                        </div>
                        <div className="modal-actions" style={{ alignSelf: 'end' }}>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => {
                              setReverseId(null);
                              setReverseReason('');
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn danger"
                            disabled={reverse.isPending || !reverseReason.trim()}
                          >
                            {reverse.isPending ? 'Reversing…' : 'Reverse collection'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                  <div className="right" style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                    <span
                      className="amt"
                      style={{
                        color: reversed ? 'var(--ink-300)' : 'var(--lime-fg)',
                        textDecoration: reversed ? 'line-through' : undefined,
                      }}
                    >
                      −{formatNPR(s.amount_cents)}
                    </span>
                    {!reversed && reverseId !== s.id && can('house_tab:settle') && (
                      <button
                        type="button"
                        className="linklike"
                        onClick={() => {
                          setReverseId(s.id);
                          setReverseReason('');
                        }}
                      >
                        <Undo2 size={13} strokeWidth={1.5} /> Reverse
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </Section>

          {/* Footer actions */}
          <div
            className="modal-actions"
            style={{ marginTop: 14, borderTop: '1px solid var(--ink-800)', paddingTop: 14 }}
          >
            {can('house_tab:update') &&
              (t.is_active ? (
                <button
                  type="button"
                  className="btn"
                  onClick={async () => {
                    try {
                      await update.mutateAsync({ id, patch: { is_active: false } });
                      toast.info('Account archived');
                    } catch (e: unknown) {
                      toast.error('Could not archive', (e as { message?: string }).message);
                    }
                  }}
                  disabled={update.isPending}
                >
                  <Archive size={14} strokeWidth={1.5} /> Archive
                </button>
              ) : (
                <button
                  type="button"
                  className="btn"
                  onClick={async () => {
                    try {
                      await update.mutateAsync({ id, patch: { is_active: true } });
                      toast.info('Account reactivated');
                    } catch (e: unknown) {
                      toast.error('Could not reactivate', (e as { message?: string }).message);
                    }
                  }}
                  disabled={update.isPending}
                >
                  <RefreshCw size={14} strokeWidth={1.5} /> Reactivate
                </button>
              ))}
            {can('house_tab:delete') && (
              <button
                type="button"
                className="btn danger"
                disabled={balance !== 0 || del.isPending}
                title={balance !== 0 ? 'settle the balance first' : undefined}
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Delete tab?',
                    message: (
                      <>
                        Permanently remove the <strong>{t.name}</strong> credit account.
                        Only allowed when the balance is zero.
                      </>
                    ),
                    confirmLabel: 'Delete account',
                    danger: true,
                  });
                  if (!ok) return;
                  try {
                    await del.mutateAsync(id);
                    toast.info('Account deleted');
                    onClose();
                  } catch (e: unknown) {
                    toast.error('Could not delete', (e as { message?: string }).message);
                  }
                }}
              >
                <Trash2 size={14} strokeWidth={1.5} /> Delete
              </button>
            )}
            <button type="button" className="btn primary" onClick={onClose}>
              <X size={14} strokeWidth={1.5} /> Close
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 14 }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--ink-400)',
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

export type { HouseTab };
